import React from "react";

function CodeBlock({ text, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <div className="stt-code">
      <pre className="stt-pre" aria-label="code snippet">
        <code>{text}</code>
      </pre>
      <button className="stt-btn stt-btn-outline" onClick={copy}>
        {copied ? "Copied!" : label}
      </button>
    </div>
  );
}

export default function SoftapiDocs() {
  const installText = `pip install softapi

# create a new project
softapi new my-app --fastapi --jwt --db sqlite

# or with Postgres
softapi new my-app --fastapi --jwt --db postgres`;

  const projectTreeText = `my-app/
  app/
    main.py
    api/
      routes.py
    core/
      config.py        # settings via env vars
      security.py      # JWT helpers
    db/
      session.py       # engine + session
      models.py
      schemas.py
    deps/
      auth.py
  .env.example
  pyproject.toml
  README.md`;

  const envText = `# server
APP_NAME=softapi-app
APP_ENV=dev

# security
SECRET_KEY=change_me
ACCESS_TOKEN_EXPIRE_MINUTES=60

# db (SQLite)
DATABASE_URL=sqlite+aiosqlite:///./app.db

# db (Postgres)
# DATABASE_URL=postgresql+asyncpg://user:pass@localhost:5432/mydb`;

  const runText = `cd my-app
uvicorn app.main:app --reload
# open http://localhost:8000/docs`;

  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        {/* TOC */}
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#intro">Introduction</a>
          <a href="#install">Install & Create</a>
          <a href="#structure">Project Structure</a>
          <a href="#env">Environment & Auth</a>
          <a href="#run">Run & DB Options</a>
          <a href="#troubleshoot">Troubleshooting</a>
        </aside>

        {/* Content */}
        <article>
          {/* Hero */}
          <header className="stt-hero">
            <div className="stt-pill">softapi • Docs</div>
            <h1 className="stt-h1">Scaffold backends without boilerplate</h1>
            <p className="stt-lead">
              Spin up a FastAPI project with JWT + DB and a clean layout in one command.
            </p>
          </header>

          {/* Intro */}
          <section id="intro" className="stt-section">
            <h2 className="stt-h2">What is softapi?</h2>
            <p className="stt-p">
              A simple CLI that generates a production-lean FastAPI skeleton with
              <strong> JWT auth</strong>, <strong>database wiring</strong> (SQLite or Postgres),
              and a sane file layout so you can focus on writing routes, not boilerplate.
            </p>
          </section>

          {/* Install */}
          <section id="install" className="stt-section stt-card">
            <h2 className="stt-card-title">Install & Create</h2>
            <div className="stt-card-body">
              <CodeBlock text={installText} label="Copy commands" />
            </div>
          </section>

          {/* Project Structure */}
          <section id="structure" className="stt-section stt-card">
            <h2 className="stt-card-title">Project Structure</h2>
            <div className="stt-card-body">
              {/* Copy plain-text tree */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <button
                  className="stt-btn stt-btn-outline"
                  onClick={() => navigator.clipboard.writeText(projectTreeText)}
                >
                  Copy as text
                </button>
              </div>

              {/* Pretty tree */}
              <div className="stt-tree" aria-label="project folder tree">
                <ul>
                  <li className="dir">
                    <span>my-app</span>
                    <ul>
                      <li className="dir">
                        <span>app</span>
                        <ul>
                          <li className="file"><span>main.py</span></li>
                          <li className="dir">
                            <span>api</span>
                            <ul>
                              <li className="file"><span>routes.py</span></li>
                            </ul>
                          </li>
                          <li className="dir">
                            <span>core</span>
                            <ul>
                              <li className="file">
                                <span>config.py</span><span className="hint"># settings via env vars</span>
                              </li>
                              <li className="file">
                                <span>security.py</span><span className="hint"># JWT helpers</span>
                              </li>
                            </ul>
                          </li>
                          <li className="dir">
                            <span>db</span>
                            <ul>
                              <li className="file">
                                <span>session.py</span><span className="hint"># engine + session</span>
                              </li>
                              <li className="file"><span>models.py</span></li>
                              <li className="file"><span>schemas.py</span></li>
                            </ul>
                          </li>
                          <li className="dir">
                            <span>deps</span>
                            <ul>
                              <li className="file"><span>auth.py</span></li>
                            </ul>
                          </li>
                        </ul>
                      </li>
                      <li className="file"><span>.env.example</span></li>
                      <li className="file"><span>pyproject.toml</span></li>
                      <li className="file"><span>README.md</span></li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Environment & Auth */}
          <section id="env" className="stt-section stt-card">
            <h2 className="stt-card-title">Environment & Auth</h2>
            <div className="stt-card-body">
              <p className="stt-p">Copy and edit <code>.env.example</code> → <code>.env</code>.</p>
              <CodeBlock text={envText} label="Copy .env" />
              <p className="stt-p">
                JWT auth is pre-wired: <code>/auth/login</code> issues tokens; protect routes with a
                dependency that verifies the bearer token.
              </p>
            </div>
          </section>

          {/* Run & DB Options (2-up like the others) */}
          <section id="run" className="stt-section stt-grid-2">
            <article className="stt-card">
              <h2 className="stt-card-title">Run & Try</h2>
              <div className="stt-card-body">
                <CodeBlock text={runText} label="Copy run" />
              </div>
            </article>

            <article className="stt-card">
              <h2 className="stt-card-title">Database Options</h2>
              <div className="stt-card-body">
                <ul className="stt-list">
                  <li><strong>SQLite</strong> for quick starts and demos (async driver: aiosqlite).</li>
                  <li><strong>Postgres</strong> for staging/production (driver: asyncpg).</li>
                </ul>
              </div>
            </article>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshoot" className="stt-section stt-card">
            <h2 className="stt-card-title">Troubleshooting</h2>
            <div className="stt-card-body">
              <ul className="stt-list">
                <li><em>ModuleNotFoundError</em>: activate your venv or <code>pip install -U softapi</code>.</li>
                <li><em>DB connect errors</em>: verify <code>DATABASE_URL</code> and driver (aiosqlite/asyncpg installed).</li>
                <li><em>JWT invalid</em>: ensure <code>SECRET_KEY</code> set and pass <code>Authorization: Bearer &lt;token&gt;</code>.</li>
              </ul>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
